import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, MessageCircle, Mail, Home } from 'lucide-react';
import { publicApi } from '../lib/api';
import LotusLogo from '../components/LotusLogo';
import { contactInfo } from '../data/mock';

const PayReturn = ({ mode = 'return' }) => {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const ref = params.get('ref');
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    if (!ref) { setState({ loading: false, data: null }); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const r = await publicApi.get(`/deposits/${ref}`);
        setState({ loading: false, data: r.data });
        // If still pending on return page, retry briefly (PayFast ITN may arrive a beat later)
        if (mode === 'return' && r.data.status === 'pending' && tries < 6) {
          tries++;
          setTimeout(poll, 2500);
        }
      } catch {
        setState({ loading: false, data: null });
      }
    };
    poll();
  }, [ref, mode]);

  const { loading, data } = state;
  const isPaid = data?.status === 'paid';
  const isCancel = mode === 'cancel';
  const isFail = data && !isPaid && !isCancel;

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-6"
      style={{ background: 'linear-gradient(135deg,#f4a48c 0%,#e9a0aa 25%,#d29ac1 55%,#b8a4d8 80%,#a5b0e6 100%)' }}>
      <div className="max-w-lg w-full bg-white/20 backdrop-blur-md border border-white/30 rounded-3xl p-8 md:p-10 shadow-2xl text-white text-center">
        <div className="flex justify-center mb-4"><LotusLogo className="w-16 h-16" /></div>
        <p className="tracking-[0.3em] text-[10px] opacity-90 mb-2" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
          BLISSFULL MOBILE SPA
        </p>

        {loading && (
          <>
            <Loader2 size={40} className="mx-auto animate-spin mb-4" />
            <h1 className="font-light" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '2rem' }}>Confirming your deposit</h1>
            <p className="mt-3 text-white/90" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.05rem' }}>
              Just a moment while PayFast lets us know all went through&hellip;
            </p>
          </>
        )}

        {!loading && isPaid && (
          <>
            <CheckCircle2 size={48} className="mx-auto mb-4" />
            <h1 className="font-light" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '2.2rem' }}>Deposit confirmed &#10024;</h1>
            <p className="mt-3 text-white/95" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.1rem' }}>
              Thank you {data?.name}! Your slot for <b>{data?.treatment}</b> is secured.
            </p>
            <p className="mt-1 text-white/85" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Balance of R{(Number(data?.treatment_price || 0) - Number(data?.deposit_amount || 0)).toLocaleString()} due to Dawn on the day. A confirmation email is on its way.
            </p>
          </>
        )}

        {!loading && isCancel && (
          <>
            <XCircle size={48} className="mx-auto mb-4" />
            <h1 className="font-light" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '2.2rem' }}>Payment cancelled</h1>
            <p className="mt-3 text-white/90" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.05rem' }}>
              No problem &mdash; your slot isn&apos;t confirmed yet. Try again from the site or WhatsApp Dawn.
            </p>
          </>
        )}

        {!loading && isFail && (
          <>
            <Loader2 size={40} className="mx-auto mb-4" />
            <h1 className="font-light" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '2rem' }}>Awaiting confirmation</h1>
            <p className="mt-3 text-white/90" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.05rem' }}>
              We haven&apos;t received PayFast&apos;s confirmation yet. Once it arrives you&apos;ll get an email. If nothing arrives in 30 minutes please WhatsApp Dawn.
            </p>
          </>
        )}

        {!loading && !data && (
          <>
            <XCircle size={40} className="mx-auto mb-4" />
            <h1 className="font-light" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '2rem' }}>Not found</h1>
            <p className="mt-3 text-white/90" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.05rem' }}>
              We couldn&apos;t find this deposit. Please start again from the booking page.
            </p>
          </>
        )}

        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <button onClick={() => nav('/')}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/70 text-white text-xs tracking-[0.25em] bg-white/5 hover:bg-white hover:text-rose-500 transition-all"
            style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
            <Home size={13} /> BACK TO SITE
          </button>
          <a href={`https://wa.me/${contactInfo.whatsappIntl}`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/70 text-white text-xs tracking-[0.25em] bg-white/5 hover:bg-white hover:text-rose-500 transition-all"
            style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
            <MessageCircle size={13} /> WHATSAPP DAWN
          </a>
          <a href={`mailto:${contactInfo.email}`}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/70 text-white text-xs tracking-[0.25em] bg-white/5 hover:bg-white hover:text-rose-500 transition-all"
            style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
            <Mail size={13} /> EMAIL DAWN
          </a>
        </div>
      </div>
    </div>
  );
};

export default PayReturn;
